import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Reveal, MaskLines } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

export default function InsightPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    (async () => {
      try {
        const { data } = await api.get(`/posts/${slug}`);
        setPost(data);
        setStatus("ok");
      } catch (e) {
        setStatus("notfound");
      }
    })();
  }, [slug]);

  if (status === "loading")
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center pt-32">
        <Loader2 className="animate-spin text-blue" size={24} />
      </div>
    );

  if (status === "notfound")
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center pt-32 px-6 text-center">
        <h1 className="font-serif font-bold text-4xl text-blue">This insight has moved.</h1>
        <Link to="/insights" className="eyebrow text-amber-deep mt-6 inline-flex items-center gap-2">
          <ArrowLeft size={15} /> Back to Insights
        </Link>
      </div>
    );

  const paragraphs = post.body.split(/\n\s*\n/).filter((p) => p.trim());

  return (
    <>
      <article className="bg-cream grain pt-40 pb-20 md:pt-52 md:pb-28">
        <div className="container-editorial max-w-3xl">
          <Reveal>
            <Link
              to="/insights"
              data-testid="back-to-insights"
              className="eyebrow text-[0.6rem] text-slate hover:text-amber-deep inline-flex items-center gap-2 mb-10"
            >
              <ArrowLeft size={14} /> Insights
            </Link>
          </Reveal>
          <p className="eyebrow text-amber-deep mb-6">{fmtDate(post.created_at)}</p>
          <h1 className="font-serif font-bold text-4xl md:text-6xl text-blue leading-[1.02] tracking-tight">
            <MaskLines lines={[post.title]} delay={0.1} />
          </h1>
          <div className="h-px bg-blue/15 my-12" />
          <div data-testid="post-body-content" className="space-y-6">
            {paragraphs.map((para, i) => (
              <Reveal key={i} delay={0.04 * i}>
                <p className="font-body text-lg text-charcoal/85 leading-relaxed whitespace-pre-line">
                  {para}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </article>
      <CTASection />
    </>
  );
}
